import {
  STUDIO_WORKSPACE_GROUP_PROPERTY,
  STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE,
} from 'src/constants/studioFtux';

/**
 * Environment cohort (LocalStack / Railway preview), not a product cohort.
 * Must match backend `posthogservice.LocalstackWorkspaceGroupTrait` / Value
 * (`"localstack"` / `"true"`). To turn a flag on for every ephemeral stack,
 * add a release condition group on this property in the staging PostHog project.
 */
export const LOCALSTACK_WORKSPACE_GROUP_PROPERTY = 'localstack';
export const LOCALSTACK_WORKSPACE_GROUP_PROPERTY_VALUE = 'true';

/**
 * QA-twin cohort. Must match backend `posthogservice.TwinGroupTrait` / Value
 * (`"twin"` / `"true"`). Stamp only — do not change flag rollouts.
 */
export const TWIN_WORKSPACE_GROUP_PROPERTY = 'twin';
export const TWIN_WORKSPACE_GROUP_PROPERTY_VALUE = 'true';

/**
 * Workspace group properties for PostHog client `group()` / SSR `getAllFlags`.
 * Studio portals include the cohort trait so flags targeting `studio="true"`
 * evaluate before (or without relying on) async `$groupidentify` ingest.
 * LocalStack portals include `localstack="true"` the same way — ingest is
 * async, and a new portal ID may never match stored groups.
 *
 * Traits derive from persisted PortalConfig (Studio) and the Cognito proxy
 * endpoint (LocalStack), so every dashboard load re-asserts them: a
 * `$groupidentify` the backend failed to deliver at workspace creation is
 * repaired here rather than leaving the workspace untargetable.
 */
export const getWorkspacePosthogGroupProperties = (
  portalName: string,
  isStudioUser: boolean,
  isLocalstack: boolean,
  twinSourcePortalId?: string,
): Record<string, string> => ({
  name: portalName,
  ...getStudioGroupProperties(isStudioUser),
  ...getLocalstackGroupProperties(isLocalstack),
  ...getTwinGroupProperties(twinSourcePortalId),
});

/** Options bag for posthog-node `getAllFlags` workspace evaluation. */
export const getPosthogGetAllFlagsOptions = (
  portalHeader: string,
  isStudioUser: boolean,
  isLocalstack: boolean,
  twinSourcePortalId?: string,
): {
  groups: { workspace: string };
  groupProperties?: {
    workspace: Record<string, string>;
  };
} => {
  // Twins evaluate flags as their source workspace so that any flag targeting
  // the source automatically covers its QA twins.
  const effectivePortalId = twinSourcePortalId ?? portalHeader;
  const workspaceProperties = {
    ...getStudioGroupProperties(isStudioUser),
    ...getLocalstackGroupProperties(isLocalstack),
    ...getTwinGroupProperties(twinSourcePortalId),
  };
  return {
    groups: { workspace: effectivePortalId },
    ...(Object.keys(workspaceProperties).length > 0
      ? { groupProperties: { workspace: workspaceProperties } }
      : {}),
  };
};

/**
 * Cache key so Studio, Classic, LocalStack, and twin evaluations never share a
 * cached result — they resolve different flags and carry different group
 * properties. Twin portals key on their source portal ID so flags are
 * consistently evaluated against the source cohort.
 */
export const getPosthogFlagsCacheKey = (
  portalHeader: string,
  isStudioUser: boolean,
  isLocalstack: boolean,
  twinSourcePortalId?: string,
): string => {
  const effectiveId = twinSourcePortalId ?? portalHeader;
  let key = effectiveId;
  if (isStudioUser) {
    key += ':studio';
  }
  if (isLocalstack) {
    key += ':localstack';
  }
  if (twinSourcePortalId) {
    key += ':twin';
  }
  return key;
};

const getStudioGroupProperties = (
  isStudioUser: boolean,
): Record<string, string> =>
  isStudioUser
    ? {
        [STUDIO_WORKSPACE_GROUP_PROPERTY]:
          STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE,
      }
    : {};

const getLocalstackGroupProperties = (
  isLocalstack: boolean,
): Record<string, string> =>
  isLocalstack
    ? {
        [LOCALSTACK_WORKSPACE_GROUP_PROPERTY]:
          LOCALSTACK_WORKSPACE_GROUP_PROPERTY_VALUE,
      }
    : {};

const getTwinGroupProperties = (
  twinSourcePortalId: string | undefined,
): Record<string, string> =>
  twinSourcePortalId
    ? { [TWIN_WORKSPACE_GROUP_PROPERTY]: TWIN_WORKSPACE_GROUP_PROPERTY_VALUE }
    : {};
